import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Circle, Hexagon, Pencil, Plus, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Empty, Field, NSelect, PageHeader } from "@/components/common";
import { useLeafletMap } from "@/hooks/useLeafletMap";
import { vehicleIcon } from "@/components/map/markers";
import { useFleet } from "@/context/FleetContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const COLORS = ["#EAB308", "#10B981", "#3B82F6", "#EF4444", "#A855F7"];
const blank = (type) => ({ name: "", type, center: null, radius: 500, points: [], color: COLORS[0], active: true, alert_enter: true, alert_exit: true, customer_id: "" });

export default function Geofences() {
  const { vehicles } = useFleet();
  const { isAdmin, isSeller } = useAuth();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [del, setDel] = useState(null);
  const [customers, setCustomers] = useState([]);
  const mapRef = useRef(null);
  const map = useLeafletMap(mapRef);

  const load = () => api.get("/geofences").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if (isAdmin || isSeller) api.get("/admin/customers").then((r) => setCustomers(r.data));
  }, [isAdmin, isSeller]);

  useEffect(() => {
    if (!map) return undefined;
    const g = L.layerGroup().addTo(map);
    items.filter((x) => x.id !== draft?.id).forEach((x) => {
      const style = { color: x.color, weight: 2, fillOpacity: x.active ? 0.15 : 0.04, dashArray: x.active ? null : "4 6" };
      const shape = x.type === "circle" ? L.circle(x.center, { ...style, radius: x.radius }) : L.polygon(x.points, style);
      shape.bindTooltip(x.name, { permanent: false }).addTo(g);
    });
    vehicles.filter((v) => v.lat != null).forEach((v) => L.marker([v.lat, v.lng], { icon: vehicleIcon(v, false, false) }).addTo(g));
    return () => g.remove();
  }, [map, items, vehicles, draft?.id]);

  useEffect(() => {
    if (!map || !draft) return undefined;
    const g = L.layerGroup().addTo(map);
    const style = { color: draft.color, weight: 2.5, dashArray: "6 6", fillOpacity: 0.2 };
    if (draft.type === "circle" && draft.center) L.circle(draft.center, { ...style, radius: draft.radius }).addTo(g);
    if (draft.type === "polygon" && draft.points.length) {
      (draft.points.length > 2 ? L.polygon(draft.points, style) : L.polyline(draft.points, style)).addTo(g);
      draft.points.forEach((p) => L.circleMarker(p, { radius: 4, color: draft.color, fillOpacity: 1 }).addTo(g));
    }
    const onClick = (e) => {
      const pt = [Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))];
      setDraft((d) => (d.type === "circle" ? { ...d, center: pt } : { ...d, points: [...d.points, pt] }));
    };
    map.on("click", onClick);
    map.getContainer().style.cursor = "crosshair";
    return () => {
      g.remove();
      map.off("click", onClick);
      map.getContainer().style.cursor = "";
    };
  }, [map, draft]);

  const start = (type) => setDraft({ ...blank(type), customer_id: customers[0]?.id || "" });
  const edit = (x) => {
    setDraft({ ...blank(x.type), ...x, points: x.points || [], radius: x.radius || 500 });
    if (x.type === "circle") map.flyTo(x.center, 14);
    else map.fitBounds(L.latLngBounds(x.points).pad(0.3));
  };
  const save = async () => {
    const body = { ...draft };
    try {
      if (draft.id) await api.put(`/geofences/${draft.id}`, body);
      else await api.post("/geofences", body);
      toast.success("Geofence disimpan");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = async (x, active) => {
    await api.patch(`/geofences/${x.id}/toggle`, { active });
    setItems((p) => p.map((g) => (g.id === x.id ? { ...g, active } : g)));
  };
  const remove = async () => {
    await api.delete(`/geofences/${del.id}`);
    toast.success("Geofence dihapus");
    setDel(null);
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Geofence" title="Area & Geofence" subtitle="Buat area lingkaran atau polygon. Event masuk/keluar otomatis dikirim ke Notification Center."
        actions={!draft && (
          <>
            <button onClick={() => start("circle")} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground" data-testid="geofence-add-circle"><Circle className="h-4 w-4" /> Lingkaran</button>
            <button onClick={() => start("polygon")} className="flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-bold" data-testid="geofence-add-polygon"><Hexagon className="h-4 w-4" /> Polygon</button>
          </>
        )} />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px_1fr]">
        <div className="space-y-4">
          {draft && (
            <div className="gt-card space-y-3 border-primary/50 p-4 gt-rise" data-testid="geofence-form">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold">{draft.id ? "Edit" : "Tambah"} geofence {draft.type === "circle" ? "lingkaran" : "polygon"}</div>
                <span className="text-[11px] text-muted-foreground">Klik peta untuk {draft.type === "circle" ? "titik pusat" : "menambah titik"}</span>
              </div>
              <Field label="Nama area"><input className="gt-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Gudang Utama" data-testid="geofence-name-input" /></Field>
              {(isAdmin || isSeller) && !draft.id && (
                <Field label="Customer">
                  <NSelect value={draft.customer_id} onChange={(v) => setDraft({ ...draft, customer_id: v })} testId="geofence-customer-select">
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </NSelect>
                </Field>
              )}
              {draft.type === "circle" ? (
                <Field label={`Radius · ${draft.radius} m`}>
                  <Slider value={[draft.radius]} min={50} max={5000} step={50} onValueChange={([r]) => setDraft({ ...draft, radius: r })} data-testid="geofence-radius-slider" />
                  <div className="mt-2 font-mono text-[11px] text-muted-foreground" data-testid="geofence-center">{draft.center ? `Pusat ${draft.center.join(", ")}` : "Belum ada titik pusat"}</div>
                </Field>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-xs">
                  <span className="font-mono" data-testid="geofence-point-count">{draft.points.length} titik</span>
                  <button onClick={() => setDraft({ ...draft, points: draft.points.slice(0, -1) })} className="flex items-center gap-1 font-semibold text-muted-foreground hover:text-foreground" data-testid="geofence-undo-point"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
                </div>
              )}
              <div className="flex gap-2">
                {COLORS.map((c) => <button key={c} onClick={() => setDraft({ ...draft, color: c })} className={cn("h-7 w-7 rounded-full border-2", draft.color === c ? "border-foreground" : "border-transparent")} style={{ background: c }} data-testid={`geofence-color-${c.slice(1)}`} />)}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2"><Switch checked={draft.alert_enter} onCheckedChange={(x) => setDraft({ ...draft, alert_enter: x })} data-testid="geofence-alert-enter" /> Alert masuk</label>
                <label className="flex items-center gap-2"><Switch checked={draft.alert_exit} onCheckedChange={(x) => setDraft({ ...draft, alert_exit: x })} data-testid="geofence-alert-exit" /> Alert keluar</label>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setDraft(null)} className="flex-1 rounded-xl border border-border py-2.5 text-sm font-semibold" data-testid="geofence-cancel">Batal</button>
                <button onClick={save} className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground" data-testid="geofence-save">Simpan</button>
              </div>
            </div>
          )}
          <div className="gt-card p-2" data-testid="geofence-list">
            {items.map((x) => (
              <div key={x.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-accent/60" data-testid={`geofence-item-${x.id}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${x.color}22`, color: x.color }}>{x.type === "circle" ? <Circle className="h-4 w-4" /> : <Hexagon className="h-4 w-4" />}</span>
                <button className="min-w-0 flex-1 text-left" onClick={() => (x.type === "circle" ? map.flyTo(x.center, 14) : map.fitBounds(L.latLngBounds(x.points).pad(0.3)))}>
                  <div className="truncate text-[13px] font-semibold">{x.name}</div>
                  <div className="text-[11px] text-muted-foreground">{x.type === "circle" ? `Lingkaran · ${x.radius} m` : `Polygon · ${x.points.length} titik`}</div>
                </button>
                <Switch checked={x.active} onCheckedChange={(a) => toggle(x, a)} data-testid={`geofence-toggle-${x.id}`} />
                <button onClick={() => edit(x)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`geofence-edit-${x.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDel(x)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-500" data-testid={`geofence-delete-${x.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!items.length && <Empty icon={Plus} title="Belum ada geofence" text="Tambahkan area lingkaran atau polygon." />}
          </div>
        </div>
        <div className="gt-card relative h-[480px] overflow-hidden lg:sticky lg:top-24 lg:h-[680px]" data-testid="geofence-map"><div ref={mapRef} className="absolute inset-0" /></div>
      </div>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-border bg-popover">
          <AlertDialogHeader><AlertDialogTitle>Hapus geofence?</AlertDialogTitle><AlertDialogDescription>Area "{del?.name}" akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700" data-testid="geofence-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
